import {
	object, string, number, array, mixed,
	InferType,
} from 'yup';
import dayjs from 'dayjs';
import {
	GroupAssetFormat, GroupBidStrategyInput, GroupBudgetType, GroupFrequencyUnit,
	GroupTargetsGender,
} from 'wb-spotify/graphql/generated';
import { divideCurrencyFields } from 'wb-spotify/app/routes/superGrid/common/constants';

// Option shape for selects and async selects
const optionShape = object({
	value: mixed().required('Option value is required'),
	label: string().required('Option label is required'),
});

// Multi-select: array of option objects
const multiOptionArray = array().of(optionShape);

// Age range shape for groupTargets.groupTargetsAgeRanges
const ageRangeShape = object({
	value: string()
		.required('Age range value is required')
		.test(
			'age-range-format',
			'Age range must be in format min-max (e.g. 19-24, 25-34)',
			value => {
				if (!value) return false;
				const parts = value.split('-');
				if (parts.length !== 2) return false;
				const min = Number(parts[0]);
				const max = Number(parts[1]);

				return Number.isInteger(min) && Number.isInteger(max) && min >= 0 && max >= min;
			},
		),
	label: string().required('Age range label is required'),
});

// Geo targets shape for groupTargets.groupTargetsGeoTargets
const geoTargetsShape = object({
	countryCode:   optionShape.required('Country code is required'),
	cityIds:       multiOptionArray.notRequired(),
	dmaIds:        multiOptionArray.notRequired(),
	postalCodeIds: multiOptionArray.notRequired(),
	regionIds:     multiOptionArray.notRequired(),
});

export const frontendGroupSchema = object({
	groupAssetFormat:
		string()
			.oneOf(Object.values(GroupAssetFormat), 'Invalid format')
			.required('Format is required'),
	groupBidMicroAmount: number()
		.typeError('Bid value must be a number')
		.required('Bid value is required')
		.test('bid-value-positive', 'Bid value must be positive', value => value > 0)
		.test(
			'max-2-decimals',
			'Bid value must have at most 2 decimal places',
			value => {
				if (typeof value !== 'number') return true;
				const floatValue = value / divideCurrencyFields.groupBidMicroAmount;

				return Number.isInteger(floatValue * 100);
			},
		),
	groupBidStrategy: string()
		.oneOf(Object.values(GroupBidStrategyInput), 'Invalid bid strategy')
		.required('Bid strategy is required'),
	groupBudget: object({
		microAmount: number()
			.typeError('Budget value must be a number')
			.required('Budget value is required')
			.test('budget-value-positive', 'Budget value must be positive', value => value > 0)
			.test(
				'max-2-decimals',
				'Budget value must have at most 2 decimal places',
				value => {
					if (typeof value !== 'number') return true;

					const floatValue = value / divideCurrencyFields.groupBudgetMicroAmount;

					return Number.isInteger(floatValue * 100);
				},
			),
		type: string()
			.oneOf(Object.values(GroupBudgetType), 'Invalid budget type')
			.required('Budget type is required'),
	}).required('Budget is required'),
	category:     optionShape.required('Category is required'),
	groupEndTime: string()
		.notRequired()
		.test(
			'datetime-format',
			'Group end time must be in format YYYY-MM-DD HH:mm:ss',
			value => !value || dayjs(value, 'YYYY-MM-DD HH:mm:ss', true).isValid(),
		)
		.test(
			'end-after-start',
			'Group end time must be after start time',
			function (value) {
				const { groupStartTime } = this.parent;
				if (!value || !groupStartTime) return true;
				if (!dayjs(groupStartTime, 'YYYY-MM-DD HH:mm:ss', true).isValid()) return true;
				const start = dayjs(groupStartTime, 'YYYY-MM-DD HH:mm:ss', true);
				const end = dayjs(value, 'YYYY-MM-DD HH:mm:ss', true);
				if (!end.isValid() || !start.isValid()) return true;
				if (!end.isAfter(start)) return this.createError({ message: 'End time must be after start time' });

				return true;
			},
		),
	groupFrequencyCaps: array().of(
		object({
			frequencyPeriod:
                number().typeError('Frequency period must be a number').required('Frequency period is required'),
			frequencyUnit: string()
				.oneOf(Object.values(GroupFrequencyUnit), 'Invalid frequency unit')
				.required('Frequency unit is required'),
			maxImpressions:
                number().typeError('Max impressions must be a number').required('Max impressions is required'),
		}),
	).notRequired(),
	name: string()
		.required('Name is required')
		.test({
			name:    'name-too-short',
			message: 'Name must be at least 3 characters',
			test:    value => value.length >= 3,
		}).test({
			name:    'name-too-long',
			message: 'Name must not be longer than 200 characters',
			test:    value => value.length <= 200,
		}),
	groupPacing:    string().notRequired(),
	groupStartTime: string()
		.required('Group start time is required')
		.test(
			'datetime-format',
			'Group start time must be in format YYYY-MM-DD HH:mm:ss',
			value => dayjs(value, 'YYYY-MM-DD HH:mm:ss', true).isValid(),
		),
	groupTargets: object({
		groupTargetsAgeRanges:  array().of(ageRangeShape).notRequired(),
		groupTargetsGeoTargets: geoTargetsShape.required(),
		groupTargetsGenders:    string().oneOf(Object.values(GroupTargetsGender), 'Invalid gender').notRequired(),
		groupTargetsInterests:  multiOptionArray.notRequired(),
		groupTargetsLanguage:   object({
			language: optionShape.default(undefined).notRequired(),
		}).default(undefined).notRequired(),
		groupTargetsPlatforms:                multiOptionArray.notRequired(),
		groupTargetsPodcastEpisodeTopics:     multiOptionArray.notRequired(),
		groupTargetsArtists:                  multiOptionArray.notRequired(),
		groupTargetsSensitiveTopicExclusions: object({
			sensitiveTopics: multiOptionArray.notRequired(),
		}).default(undefined).notRequired(),
		groupTargetsPlacements: multiOptionArray.required('Placements are required'),
		groupTargetsGenres:     multiOptionArray.notRequired(),
		groupTargetsPlaylists:  multiOptionArray.notRequired(),
	}).notRequired(),
});

export type SpotifySupergridGroupData = InferType<typeof frontendGroupSchema>;

export const validateFrontendGroupData = (data: Record<string, any>) => frontendGroupSchema.validate(data, { abortEarly: false });
